import { useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from "react";
import { Map, type ViewStateChangeEvent } from "react-map-gl/mapbox";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { cn } from "~/lib/utils";
import { getMapboxConfig } from "~/lib/config";
import { DEFAULT_MAP_CENTER, MAP_MAX_ZOOM, setupMapTouchContextMenu } from "~/lib/mapConstants";
import type { PlaceGeoJSON } from "~/lib/api";
import { useBreakpoint } from "~/hooks/useBreakpoint";
import { ViewportProvider } from "./viewport/ViewportContext";
import { ViewportInternalsContext } from "./viewport/context";
import MapLayoutProvider from "./layout/MapLayoutProvider";
import { useMapLayout } from "./layout/useMapLayout";
import { INTERNAL_REGION_IDS, REGION_IDS } from "./layout/constants";

type Padding = number | mapboxgl.PaddingOptions;

export interface CanvasProps {
  children?: ReactNode;
  /** GeoJSON to fit bounds to on load */
  bounds?: PlaceGeoJSON | mapboxgl.LngLatBoundsLike;
  /** Padding for initial bounds fit */
  padding?: Padding;
  /** Initial center (used if bounds not provided) */
  center?: [number, number];
  /** Initial zoom (used if bounds not provided) */
  zoom?: number;
  /** Whether map interactions are enabled (default: true) */
  interactive?: boolean;
  /** Map click handler */
  onClick?: (e: mapboxgl.MapMouseEvent) => void;
  /** Context menu handler (right-click on desktop, long-press on mobile) */
  onContextMenu?: (e: mapboxgl.MapMouseEvent) => void;
  /** Called when map finishes loading */
  onMapLoad?: (map: mapboxgl.Map) => void;
  /** Called when map moves */
  onMove?: (e: ViewStateChangeEvent) => void;
  /** Additional CSS class for the root container */
  className?: string;
}

/** Map sheet fold steps to bottom padding (in pixels) for mobile */
const SHEET_PADDING: Record<string, number> = {
  closed: 0,
  folded: 90,
  peek: 320,
  open: 320,
};

/**
 * Map.Canvas - The root component for map-based applications.
 *
 * Owns the map engine internally and provides:
 * - Viewport controls via useViewport()
 * - Layout regions via Map.Panel, Map.HeaderLeft, etc.
 * - Panel state via usePanel() and usePanelPosition()
 */
export default function Canvas({
  children,
  bounds,
  padding,
  center,
  zoom,
  interactive = true,
  onClick,
  onContextMenu,
  onMapLoad,
  onMove,
  className,
}: CanvasProps) {
  return (
    <ViewportProvider>
      <MapLayoutProvider>
        <CanvasInner
          bounds={bounds}
          padding={padding}
          center={center}
          zoom={zoom}
          interactive={interactive}
          onClick={onClick}
          onContextMenu={onContextMenu}
          onMapLoad={onMapLoad}
          onMove={onMove}
          className={className}
        >
          {children}
        </CanvasInner>
      </MapLayoutProvider>
    </ViewportProvider>
  );
}

/** Inner component that consumes contexts */
function CanvasInner({
  children,
  bounds,
  padding,
  center,
  zoom,
  interactive = true,
  onClick,
  onContextMenu,
  onMapLoad,
  onMove,
  className,
}: CanvasProps) {
  const { sheetStep } = useMapLayout();
  const { below } = useBreakpoint();
  const showPhoneLayout = below("md");
  const showMobilePanel = below("lg");
  const viewportInternals = useContext(ViewportInternalsContext);
  const mapboxConfig = getMapboxConfig();
  const touchContextMenuCleanup = useRef<(() => void) | null>(null);

  // Cleanup touch context menu on unmount
  useEffect(() => {
    return () => touchContextMenuCleanup.current?.();
  }, []);

  // On phones, add bottom padding based on sheet fold state (tablets have centered sheet)
  const bottomPadding = showPhoneLayout && sheetStep ? (SHEET_PADDING[sheetStep] ?? 0) : 0;

  const initialViewState = useMemo(
    () => ({
      longitude: center?.[0] ?? DEFAULT_MAP_CENTER[0],
      latitude: center?.[1] ?? DEFAULT_MAP_CENTER[1],
      zoom: zoom ?? 8,
    }),
    [center, zoom],
  );

  // Handle map load - register with viewport context and fit bounds
  const handleLoad = useCallback(
    (e: mapboxgl.MapboxEvent) => {
      const map = e.target;

      // Register map with viewport context
      if (viewportInternals) {
        (viewportInternals.mapRef as React.MutableRefObject<mapboxgl.Map | null>).current = map;
        viewportInternals.setIsReady(true);
      }

      // Setup touch context menu (long-press → contextmenu)
      // Always set up because child components may register handlers directly on the map
      touchContextMenuCleanup.current = setupMapTouchContextMenu(map);

      // Fit bounds if provided
      if (bounds) {
        const lngLatBounds = boundsFromGeoJSONOrBounds(bounds);
        if (lngLatBounds) {
          map.fitBounds(lngLatBounds, {
            padding: padding ?? 80,
            maxZoom: MAP_MAX_ZOOM,
            duration: 0,
          });
        }
      }

      onMapLoad?.(map);
    },
    [bounds, padding, onMapLoad, viewportInternals],
  );

  return (
    <div className={cn("@container relative h-full w-full overflow-hidden", className)}>
      {/* Map layer - full bleed, underneath everything */}
      <div className="absolute inset-0 z-0">
        <Map
          mapboxAccessToken={mapboxConfig.token}
          mapStyle={mapboxConfig.style}
          initialViewState={initialViewState}
          onLoad={handleLoad}
          onClick={interactive ? onClick : undefined}
          onContextMenu={interactive ? onContextMenu : undefined}
          onMove={onMove}
          reuseMaps
          style={{ width: "100%", height: "100%" }}
          mapLib={mapboxgl}
          scrollZoom={interactive}
          boxZoom={interactive}
          dragRotate={interactive}
          dragPan={interactive}
          keyboard={interactive}
          doubleClickZoom={interactive}
          touchZoomRotate={interactive}
          touchPitch={interactive}
        >
          {children}
        </Map>
      </div>

      {/* UI layer - flex for header + grid */}
      <div
        className={cn(
          "absolute inset-0 pointer-events-none",
          "flex flex-col gap-4 p-4 @3xl:p-5 @3xl:pb-6 @5xl:p-6 @5xl:pb-9",
          "transition-[padding] duration-200 ease-out",
        )}
        style={{ paddingBottom: bottomPadding || undefined }}
      >
        <header className="relative z-app-bar flex items-center justify-between gap-2 [&>*]:pointer-events-auto [&:has(>*:first-child:empty):has(>*:last-child:empty)]:hidden">
          <div id={REGION_IDS.headerLeft} className="flex items-center gap-2" />
          <div id={REGION_IDS.headerRight} className="flex flex-row-reverse items-center gap-2" />
        </header>
        <div
          className={cn("flex-1 min-h-0 grid gap-4 grid-rows-[1fr_auto]", {
            "grid-cols-[1fr_auto] [grid-template-areas:'._.'_'messages_controls']": showMobilePanel,
            "grid-cols-[auto_1fr_auto] [grid-template-areas:'panel_._.'_'panel_messages_controls']": !showMobilePanel,
          })}
        >
          <div
            id={REGION_IDS.panel}
            className="[grid-area:panel] relative w-96 @7xl:w-[420px] pb-8 z-floating-panel [&>*]:pointer-events-auto empty:hidden"
          />
          <div
            id={REGION_IDS.messages}
            className={cn("[grid-area:messages] relative z-fab self-end [&>*]:pointer-events-auto", {
              "justify-self-center": !showMobilePanel,
            })}
          />
          <div
            id={REGION_IDS.controls}
            className="[grid-area:controls] relative z-fab self-end flex flex-col gap-2 [&>*]:pointer-events-auto"
          />
        </div>
      </div>

      {/* Mobile panel - rendered last so it appears above controls */}
      <div id={INTERNAL_REGION_IDS.panelMobile} className="empty:hidden" />
    </div>
  );
}

/** Converts GeoJSON FeatureCollection or LngLatBounds to mapboxgl.LngLatBounds */
function boundsFromGeoJSONOrBounds(input: PlaceGeoJSON | mapboxgl.LngLatBoundsLike): mapboxgl.LngLatBounds | null {
  if (isGeoJSON(input)) {
    if (input.features.length === 0) return null;

    const validCoords = input.features
      .map((f) => f.geometry.coordinates)
      .filter(
        (c): c is [number, number] =>
          Array.isArray(c) &&
          c.length === 2 &&
          typeof c[0] === "number" &&
          typeof c[1] === "number" &&
          !isNaN(c[0]) &&
          !isNaN(c[1]),
      );

    if (validCoords.length === 0) return null;

    const bounds = new mapboxgl.LngLatBounds(validCoords[0], validCoords[0]);
    validCoords.slice(1).forEach((coord) => bounds.extend(coord));
    return bounds;
  }

  return mapboxgl.LngLatBounds.convert(input);
}

function isGeoJSON(input: PlaceGeoJSON | mapboxgl.LngLatBoundsLike): input is PlaceGeoJSON {
  return typeof input === "object" && input !== null && "type" in input && input.type === "FeatureCollection";
}
